import { useTranslation } from 'react-i18next';
import { VdsButton, VdsNotification } from '@volt/volt-extended-components-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from '@casops/routes/root/components/link/Link';
import RootPageContainer from '../components/root-page-container/RootPageContainer';
import RootPageCard from '../components/root-page-card/RootPageCard';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { LOGIN_PAGE, RESET_PASSWORD_PAGE, RESET_PASSWORD_EMAIL } from '@casops/shared/constants';
import { ForgotPasswordModel, useForgotPasswordMutation } from '@casops/store/apis/authApi';
import { useNavigate } from 'react-router-dom';
import { useCanSubmit } from '@casops/hooks/useCanSubmit';

type FormModel = ForgotPasswordModel;

const defaultValues: FormModel = {
  email: '',
};

export function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | boolean>(false);
  const [forgotPasswordChange, { isLoading }] = useForgotPasswordMutation();

  const schema = useMemo(() => {
    return z.object({
      email: z
        .string()
        .min(1, { message: t('EnterAnEmail') })
        .email({ message: t('EnterValidEmail') }),
    });
  }, [t]);

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    watch,
    clearErrors,
  } = useForm<FormModel>({
    defaultValues,
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const { canSubmit, setDisableSubmit } = useCanSubmit(isSubmitting, isLoading);

  const onSubmit = useCallback(
    async (data: FormModel) => {
      setErrorMessage(false);

      await forgotPasswordChange(data)
        .unwrap()
        .then(() => {
          setDisableSubmit(true);
          sessionStorage.setItem(RESET_PASSWORD_EMAIL, data.email);
          navigate(RESET_PASSWORD_PAGE);
        })
        .catch(error => {
          setErrorMessage(error.data);
        });
    },
    [forgotPasswordChange, navigate, setDisableSubmit],
  );

  const watchEmail = watch('email');

  // Add this callback to clear validation errors when typing
  useEffect(() => {
    if (watchEmail) {
      clearErrors('email');
    }
  }, [watchEmail, clearErrors]);

  return (
    <RootPageContainer>
      <RootPageCard
        subHeading={t('ResetPassword')}
        subText={t('ResetPasswordDescription')}
        fields={
          <>
            {errorMessage && (
              <VdsNotification variant="inline" status="danger" showCloseButton={false}>
                <span slot="body">{errorMessage}</span>
              </VdsNotification>
            )}
            <CasFormInput
              control={control}
              name="email"
              placeholder={t('YourEmailAddress')}
              type="email"
              autocomplete="email"
              disabled={!canSubmit}
              onEnterPressed={() => handleSubmit(onSubmit)()}
              size="large"
            />
          </>
        }
      >
        <VdsButton
          id="send-code-button"
          variant="primary"
          size="large"
          danger
          disabled={!canSubmit}
          onClick={handleSubmit(onSubmit)}
          isFullWidth
        >
          {t('SendCode')}
        </VdsButton>
        <Link to={LOGIN_PAGE} title={t('Cancel')}></Link>
      </RootPageCard>
    </RootPageContainer>
  );
}

export default ForgotPassword;
